import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, FileSignature, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ContractsRH() {
  const [items, setItems] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);

  const load = () => api.get("/payroll/contracts").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
    api.get("/settings").then((r) => setTypes(r.data.contract_types || [])).catch(() => {});
  }, []);

  const openNew = () => { setForm({ employe_id: "", type: "CDI", poste: "", date_debut: "", date_fin: "", salaire_base: 0, statut: "Actif", notes: "" }); setOpen(true); };
  const openEdit = (c) => { setForm({ ...c }); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    try { if (form.id) await api.put(`/payroll/contracts/${form.id}`, form); else await api.post("/payroll/contracts", form); toast.success("Contrat enregistré"); setOpen(false); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce contrat ?")) return; await api.delete(`/payroll/contracts/${id}`); toast.success("Supprimé"); load(); };
  const empName = (e) => `${e.prenom || ""} ${e.nom || ""}`.trim();

  const list = items || [];
  return (
    <div>
      <PageHeader title="Contrats de travail" subtitle="CDI, CDD, stages… & salaire de base par collaborateur">
        <Button onClick={openNew} data-testid="add-contract" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau contrat</Button>
      </PageHeader>

      {items === null ? <Loader /> : list.length === 0 ? (
        <EmptyState icon={FileSignature} title="Aucun contrat" subtitle="Créez le contrat d'un collaborateur pour préparer sa paie." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>N°</TableHead><TableHead>Collaborateur</TableHead><TableHead>Type</TableHead><TableHead>Poste</TableHead><TableHead>Période</TableHead><TableHead className="text-right">Salaire base</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {list.map((c) => (
                <TableRow key={c.id} className="hover:bg-slate-50" data-testid={`contract-row-${c.id}`}>
                  <TableCell className="font-mono text-xs text-slate-500">{c.numero}</TableCell>
                  <TableCell className="font-semibold">{c.employe_name}</TableCell>
                  <TableCell><span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{c.type}</span></TableCell>
                  <TableCell className="text-sm">{c.poste || "—"}</TableCell>
                  <TableCell className="text-sm">{c.date_debut ? fmtDate(c.date_debut) : "—"} → {c.date_fin ? fmtDate(c.date_fin) : "…"}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-bold">{fmtMoney(c.salaire_base)}</TableCell>
                  <TableCell><StatusBadge value={c.statut} /></TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`contract-menu-${c.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(c)}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(c.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-head">{form?.id ? "Modifier" : "Nouveau"} contrat</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Collaborateur *</Label>
                <Select value={form.employe_id} onValueChange={(v) => setForm({ ...form, employe_id: v })}><SelectTrigger className="mt-1" data-testid="contract-employe"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{empName(e)} · {e.matricule}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger className="mt-1" data-testid="contract-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Poste</Label><Input value={form.poste} onChange={(e) => setForm({ ...form, poste: e.target.value })} className="mt-1" data-testid="contract-poste" /></div>
              <div><Label className="text-xs">Date début</Label><Input type="date" value={form.date_debut || ""} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Date fin</Label><Input type="date" value={form.date_fin || ""} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Salaire de base (FCFA) *</Label><Input type="number" value={form.salaire_base} onChange={(e) => setForm({ ...form, salaire_base: Number(e.target.value) })} required className="mt-1" data-testid="contract-salaire" /></div>
              <div><Label className="text-xs">Statut</Label>
                <Select value={form.statut} onValueChange={(v) => setForm({ ...form, statut: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Actif", "Suspendu", "Terminé"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div className="col-span-2"><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" /></div>
              <DialogFooter className="col-span-2"><Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button type="submit" data-testid="contract-submit" className="bg-brand-600 hover:bg-brand-700 text-white">Enregistrer</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
